import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/date';
import { getStatusMeta, paymentStatusMap, packageTypeMap } from '@/lib/statusMap';
import Modal from '@/components/base/Modal';
import PaketModal from './components/PaketModal';
import OdemeModal from './components/OdemeModal';

interface ProfileRow {
  id: string;
  full_name: string;
  phone: string | null;
}

interface PackageRow {
  id: string;
  student_id: string | null;
  type: string | null;
  name: string | null;
  total_sessions: number;
  used_sessions: number;
  remaining_sessions: number;
  payment_status: string;
  start_date: string | null;
  end_date: string | null;
  note: string | null;
  created_at: string;
}

interface PaymentRow {
  id: string;
  package_id: string | null;
  student_id: string | null;
  amount: number | null;
  status: string;
  date: string | null;
  note: string | null;
  created_at: string;
}

const moneyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export default function Odeme() {
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paketOpen, setPaketOpen] = useState(false);
  const [editPaket, setEditPaket] = useState<PackageRow | null>(null);
  const [odemeOpen, setOdemeOpen] = useState(false);
  const [editOdeme, setEditOdeme] = useState<PaymentRow | null>(null);
  const [confirmDeletePaket, setConfirmDeletePaket] = useState<PackageRow | null>(null);
  const [confirmDeleteOdeme, setConfirmDeleteOdeme] = useState<PaymentRow | null>(null);
  const [tab, setTab] = useState<'paketler' | 'odemeler'>('paketler');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        { data: pkData, error: pkError },
        { data: payData, error: payError },
        { data: profData, error: profError },
      ] = await Promise.all([
        supabase.from('zehrahoca_packages').select('*').order('created_at', { ascending: false }),
        supabase.from('zehrahoca_payments').select('*').order('date', { ascending: false }),
        supabase.from('zehrahoca_profiles').select('id, full_name, phone').eq('role', 'ogrenci'),
      ]);
      if (pkError) throw pkError;
      if (payError) throw payError;
      if (profError) throw profError;

      setPackages((pkData ?? []) as PackageRow[]);
      setPayments((payData ?? []) as PaymentRow[]);
      setProfiles((profData ?? []) as ProfileRow[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handleChange = () => fetchAll();
    window.addEventListener('zehrahoca:odeme-changed', handleChange);
    return () => window.removeEventListener('zehrahoca:odeme-changed', handleChange);
  }, [fetchAll]);

  const profileMap = useMemo(() => {
    const m: Record<string, ProfileRow> = {};
    profiles.forEach((p) => { m[p.id] = p; });
    return m;
  }, [profiles]);

  const kpi = useMemo(() => {
    const totalCollected = payments
      .filter((p) => p.status === 'odendi')
      .reduce((s, p) => s + Number(p.amount ?? 0), 0);
    const pending = payments
      .filter((p) => p.status === 'bekliyor' || p.status === 'kismi' || p.status === 'gecikti')
      .reduce((s, p) => s + Number(p.amount ?? 0), 0);
    const activePackages = packages.filter((p) => p.remaining_sessions > 0).length;
    const endingSoon = packages.filter((p) => p.remaining_sessions > 0 && p.remaining_sessions <= 2).length;
    return { totalCollected, pending, activePackages, endingSoon };
  }, [packages, payments]);

  function studentName(id: string | null): string {
    if (!id) return '—';
    return profileMap[id]?.full_name ?? 'Bilinmeyen Öğrenci';
  }

  function handleNewPaket() {
    setEditPaket(null);
    setPaketOpen(true);
  }

  function handleEditPaket(p: PackageRow) {
    setEditPaket(p);
    setPaketOpen(true);
  }

  function handleNewOdeme() {
    setEditOdeme(null);
    setOdemeOpen(true);
  }

  function handleEditOdeme(p: PaymentRow) {
    setEditOdeme(p);
    setOdemeOpen(true);
  }

  async function doDeletePaket() {
    if (!confirmDeletePaket) return;
    try {
      const { error: delError } = await supabase.from('zehrahoca_packages').delete().eq('id', confirmDeletePaket.id);
      if (delError) throw delError;
      setConfirmDeletePaket(null);
      window.dispatchEvent(new Event('zehrahoca:odeme-changed'));
    } catch (err) {
      setError((err as Error).message || 'Silme işlemi başarısız oldu.');
      setConfirmDeletePaket(null);
    }
  }

  async function doDeleteOdeme() {
    if (!confirmDeleteOdeme) return;
    try {
      const { error: delError } = await supabase.from('zehrahoca_payments').delete().eq('id', confirmDeleteOdeme.id);
      if (delError) throw delError;
      setConfirmDeleteOdeme(null);
      window.dispatchEvent(new Event('zehrahoca:odeme-changed'));
    } catch (err) {
      setError((err as Error).message || 'Silme işlemi başarısız oldu.');
      setConfirmDeleteOdeme(null);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Ödeme ve paketler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Veriler yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={fetchAll} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Ödeme / Paket Takibi</h1>
          <p className="mt-1 text-sm text-foreground-600">Öğrenci paketlerini ve tahsilatları tek yerden yönetin.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={handleNewOdeme} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-600 cursor-pointer">
            <i className="ri-add-line text-base" /> Ödeme Ekle
          </button>
          <button type="button" onClick={handleNewPaket} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-add-line text-base" /> Paket Ekle
          </button>
        </div>
      </div>

      {/* KPI kartları */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500">
              <i className="ri-money-dollar-circle-line text-xl" />
            </div>
          </div>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Toplam Tahsilat</p>
          <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">{moneyFormatter.format(kpi.totalCollected)}</p>
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-500">
            <i className="ri-time-line text-xl" />
          </div>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Bekleyen Tahsilat</p>
          <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">{moneyFormatter.format(kpi.pending)}</p>
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
            <i className="ri-box-3-line text-xl" />
          </div>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Aktif Paket</p>
          <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">{kpi.activePackages}</p>
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 text-red-500">
            <i className="ri-alarm-warning-line text-xl" />
          </div>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Bitmek Üzere</p>
          <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">{kpi.endingSoon}</p>
        </div>
      </div>

      {/* Sekmeler */}
      <div className="flex items-center gap-1 rounded-full bg-background-100 p-1 w-fit">
        <button
          type="button"
          onClick={() => setTab('paketler')}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition cursor-pointer whitespace-nowrap ${tab === 'paketler' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-600 hover:text-foreground-900'}`}
        >
          Paketler ({packages.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('odemeler')}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition cursor-pointer whitespace-nowrap ${tab === 'odemeler' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-600 hover:text-foreground-900'}`}
        >
          Ödeme Geçmişi ({payments.length})
        </button>
      </div>

      {tab === 'paketler' ? (
        packages.length === 0 ? (
          <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-box-3-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-semibold text-foreground-700">Henüz paket bulunmuyor.</p>
            <p className="mt-1 text-xs text-foreground-500">İlk paketi ekleyerek başlayın.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-background-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-50 text-left text-xs font-semibold text-foreground-500">
                    <th className="px-5 py-3.5">Öğrenci</th>
                    <th className="px-5 py-3.5">Paket</th>
                    <th className="px-5 py-3.5">Seans Kullanımı</th>
                    <th className="px-5 py-3.5">Ödeme Durumu</th>
                    <th className="px-5 py-3.5">Geçerlilik</th>
                    <th className="px-5 py-3.5 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((p) => {
                    const status = getStatusMeta(p.payment_status, paymentStatusMap);
                    const pct = p.total_sessions > 0 ? Math.round((p.used_sessions / p.total_sessions) * 100) : 0;
                    const initial = studentName(p.student_id).trim().charAt(0).toUpperCase();
                    return (
                      <tr key={p.id} className="border-b border-background-100 last:border-0 hover:bg-background-50/60">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                              {initial}
                            </div>
                            <div>
                              <p className="font-semibold text-foreground-950">{studentName(p.student_id)}</p>
                              {p.type && <p className="text-xs text-foreground-500">{packageTypeMap[p.type] ?? p.type}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <p className="font-medium text-foreground-900">{p.name ?? '—'}</p>
                        </td>
                        <td className="px-5 py-4">
                          <div className="max-w-[180px]">
                            <div className="mb-1.5 flex items-center justify-between text-xs">
                              <span className="text-foreground-600">{p.used_sessions}/{p.total_sessions} seans</span>
                              <span className={`font-semibold ${p.remaining_sessions <= 2 ? 'text-red-500' : 'text-foreground-500'}`}>
                                {p.remaining_sessions} kaldı
                              </span>
                            </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-background-100">
                              <div
                                className={`h-full rounded-full transition-all ${p.remaining_sessions <= 2 ? 'bg-red-500' : 'bg-primary-500'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${status.classes}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-xs text-foreground-600">
                          {p.start_date ? formatDate(p.start_date) : '—'}
                          {p.end_date && <span className="text-foreground-400"> → {formatDate(p.end_date)}</span>}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-1.5">
                            <button type="button" onClick={() => handleEditPaket(p)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200" title="Düzenle">
                              <i className="ri-edit-line text-base" />
                            </button>
                            <button type="button" onClick={() => setConfirmDeletePaket(p)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100" title="Sil">
                              <i className="ri-delete-bin-line text-base" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        payments.length === 0 ? (
          <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-money-dollar-circle-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-semibold text-foreground-700">Henüz ödeme kaydı yok.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-background-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-50 text-left text-xs font-semibold text-foreground-500">
                    <th className="px-5 py-3.5">Tarih</th>
                    <th className="px-5 py-3.5">Öğrenci</th>
                    <th className="px-5 py-3.5">Tutar</th>
                    <th className="px-5 py-3.5">Durum</th>
                    <th className="px-5 py-3.5">Not</th>
                    <th className="px-5 py-3.5 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const status = getStatusMeta(p.status, paymentStatusMap);
                    return (
                      <tr key={p.id} className="border-b border-background-100 last:border-0 hover:bg-background-50/60">
                        <td className="px-5 py-4 text-foreground-700">{p.date ? formatDate(p.date) : '—'}</td>
                        <td className="px-5 py-4 font-semibold text-foreground-950">{studentName(p.student_id)}</td>
                        <td className="px-5 py-4 font-semibold text-foreground-950">{moneyFormatter.format(Number(p.amount ?? 0))}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${status.classes}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-xs text-foreground-600 max-w-[200px] truncate">{p.note ?? '—'}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-1.5">
                            <button type="button" onClick={() => handleEditOdeme(p)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200" title="Düzenle">
                              <i className="ri-edit-line text-base" />
                            </button>
                            <button type="button" onClick={() => setConfirmDeleteOdeme(p)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100" title="Sil">
                              <i className="ri-delete-bin-line text-base" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      <PaketModal open={paketOpen} onClose={() => setPaketOpen(false)} packageRow={editPaket} profiles={profiles} />
      <OdemeModal open={odemeOpen} onClose={() => setOdemeOpen(false)} payment={editOdeme} profiles={profiles} packages={packages} />

      {/* Paket silme onayı */}
      <Modal open={!!confirmDeletePaket} onClose={() => setConfirmDeletePaket(null)} title="Paketi Sil" subtitle="Bu işlem geri alınamaz.">
        <div className="space-y-5">
          <p className="text-sm text-foreground-700">
            <span className="font-semibold">{studentName(confirmDeletePaket?.student_id ?? null)}</span> öğrencisine ait
            <span className="font-semibold"> {confirmDeletePaket?.name ?? 'paket'}</span> paketini silmek istediğinize emin misiniz?
          </p>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button type="button" onClick={() => setConfirmDeletePaket(null)} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap">Vazgeç</button>
            <button type="button" onClick={doDeletePaket} className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 cursor-pointer whitespace-nowrap">Sil</button>
          </div>
        </div>
      </Modal>

      {/* Ödeme silme onayı */}
      <Modal open={!!confirmDeleteOdeme} onClose={() => setConfirmDeleteOdeme(null)} title="Ödemeyi Sil" subtitle="Bu işlem geri alınamaz.">
        <div className="space-y-5">
          <p className="text-sm text-foreground-700">
            <span className="font-semibold">{moneyFormatter.format(Number(confirmDeleteOdeme?.amount ?? 0))}</span> tutarındaki ödemeyi silmek istediğinize emin misiniz?
          </p>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button type="button" onClick={() => setConfirmDeleteOdeme(null)} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap">Vazgeç</button>
            <button type="button" onClick={doDeleteOdeme} className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 cursor-pointer whitespace-nowrap">Sil</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}